/**
 * Matter Report - API Service
 *
 * HTTP client for Matter Ledger and Statement report API endpoints
 */

import { api } from '@xtnd/web/shared/api';

// Request parameter types
interface MatterLedgerParams {
  matterId: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  accountType?: 'Trust' | 'Operating';
}

interface MatterStatementParams {
  matterId: string;
  startDate: string;
  endDate: string;
}

interface MatterExportParams {
  matterId: string;
  type: 'csv' | 'xls' | 'pdf';
  startDate?: string;
  endDate?: string;
  includeMetadata?: boolean;
  accountType?: 'Trust' | 'Operating';
}

class MatterReportApiService {
  /**
   * Build query string from parameters
   */
  private buildQueryString(params: Record<string, unknown>): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(item => searchParams.append(`${key}[]`, String(item)));
        } else {
          searchParams.set(key, String(value));
        }
      }
    });

    return searchParams.toString();
  }

  /**
   * Get matter ledger transactions
   */
  async getLedger(params: MatterLedgerParams) {
    const { matterId, ...queryParams } = params;
    const queryString = this.buildQueryString(queryParams as Record<string, unknown>);
    const endpoint = `/reports/matter/${matterId}/ledger${queryString ? `?${queryString}` : ''}`;

    return await api.get(endpoint);
  }

  /**
   * Get matter statement data
   */
  async getStatement(params: MatterStatementParams) {
    const { matterId, ...queryParams } = params;
    const queryString = this.buildQueryString(queryParams as Record<string, unknown>);
    const endpoint = `/reports/matter/${matterId}/statement${queryString ? `?${queryString}` : ''}`;

    return await api.get(endpoint);
  }

  /**
   * Sync matter ledger data from Clio
   * Uses the new unified replication endpoint for consistency
   */
  async syncLedger(matterId: string): Promise<{
    success: boolean;
    message: string;
    transactionsFetched: number;
    transactionsSaved: number;
  }> {
    return await api.post(`/replication/clio/matters/${matterId}/bank-transactions/sync`);
  }

  /**
   * Export matter ledger report
   */
  async exportLedger(params: MatterExportParams): Promise<void> {
    const { matterId, ...exportParams } = params;

    // Use fetch with shared API client logic for proper authentication
    const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8080').replace(/\/$/, '');
    const url = `${API_BASE_URL}/reports/matter/${matterId}/ledger/export`;

    // Get fresh token using shared API client logic
    const getAccessToken = (): string | null => localStorage.getItem('accessToken');
    const isTokenExpired = (): boolean => {
      const expiredAt = localStorage.getItem('expiredAt');
      if (!expiredAt) return true;
      return Date.now() >= parseInt(expiredAt);
    };

    const refreshAccessToken = async (): Promise<string | null> => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return null;

      try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) throw new Error('Refresh failed');

        const data = await response.json();
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('expiredAt', data.expiredAt);

        return data.accessToken;
      } catch (error) {
        // Clear all tokens on refresh failure
        localStorage.removeItem('accessToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('expiredAt');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return null;
      }
    };

    let token = getAccessToken();

    // Try to refresh token if expired
    if (token && isTokenExpired()) {
      token = await refreshAccessToken();
    }

    if (!token) {
      throw new Error('No valid access token available');
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(exportParams),
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }

    // For PDF exports, the response is JSON with a download URL
    // For CSV/Excel, the response is a binary file
    const contentType = response.headers.get('content-type') || '';

    if (params.type === 'pdf' && contentType.includes('application/json')) {
      // PDF export - get signed S3 URL from JSON response
      const data = await response.json();
      if (data.downloadUrl) {
        // Open the S3 URL in a new tab to trigger download
        window.open(data.downloadUrl, '_blank');
      } else {
        throw new Error('PDF download URL not found in response');
      }
    } else {
      // CSV/Excel export - download binary file
      // Get filename from response headers or use default with timestamp
      const contentDisposition = response.headers.get('content-disposition');
      const now = new Date();
      const timeStamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
      let filename = `matter-ledger-${matterId}-${timeStamp}.${params.type}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and trigger download
      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: contentType || 'application/octet-stream' });
      const downloadUrl = window.URL.createObjectURL(blob);

      // Create temporary download link
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    }
  }

  /**
   * Get list of matters for selection from DynamoDB
   * Fetches all matters for the tenant (not just open/pending)
   */
  async getMatters(search?: string): Promise<Array<{ id: string; description: string; number?: string; displayNumber?: string }>> {
    const queryParams = new URLSearchParams();
    if (search && search.trim()) {
      queryParams.set('search', search.trim());
    }

    const endpoint = `/clio/matters${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get<{ matters: Array<{ id: string; description: string; number?: string; displayNumber?: string }> }>(endpoint);
    return response.matters || [];
  }

  /**
   * Export matter statement report
   */
  async exportStatement(params: MatterExportParams): Promise<void> {
    const { matterId, ...exportParams } = params;

    // Use fetch with shared API client logic for proper authentication
    const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8080').replace(/\/$/, '');
    const url = `${API_BASE_URL}/reports/matter/${matterId}/statement/export`;

    // Get fresh token using shared API client logic
    const getAccessToken = (): string | null => localStorage.getItem('accessToken');
    const isTokenExpired = (): boolean => {
      const expiredAt = localStorage.getItem('expiredAt');
      if (!expiredAt) return true;
      return Date.now() >= parseInt(expiredAt);
    };

    const refreshAccessToken = async (): Promise<string | null> => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return null;

      try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) throw new Error('Refresh failed');

        const data = await response.json();
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('expiredAt', data.expiredAt);

        return data.accessToken;
      } catch (error) {
        // Clear all tokens on refresh failure
        localStorage.removeItem('accessToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('expiredAt');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return null;
      }
    };

    let token = getAccessToken();

    // Try to refresh token if expired
    if (token && isTokenExpired()) {
      token = await refreshAccessToken();
    }

    if (!token) {
      throw new Error('No valid access token available');
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(exportParams),
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }

    // Get filename from response headers or use default with timestamp
    const contentDisposition = response.headers.get('content-disposition');
    const now = new Date();
    const timeStamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    let filename = `matter-statement-${matterId}-${timeStamp}.${params.type}`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Create blob and trigger download
    const arrayBuffer = await response.arrayBuffer();
    const blob = new Blob([arrayBuffer], { type: response.headers.get('content-type') || 'application/octet-stream' });
    const downloadUrl = window.URL.createObjectURL(blob);

    // Create temporary download link
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

// Export singleton instance
export const matterReportApi = new MatterReportApiService();

// Export types for use in components
export type { MatterLedgerParams, MatterStatementParams, MatterExportParams };

